"use client";

import * as React from "react";
import { format } from "date-fns";
import { CalendarIcon, X } from "lucide-react";
import type { DateRange } from "react-day-picker";

import { cn } from "@/lib/utils";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

// CargoFL ShadCN Library — DateRangePicker (Calendar mode="range" + Popover). The single
// DatePicker is single-date only; Call Logs needs a from/to range.
export interface DateRangePickerProps {
  value?: DateRange;
  onChange?: (range: DateRange | undefined) => void;
  placeholder?: string;
  className?: string;
  id?: string;
}

function DateRangePicker({
  value,
  onChange,
  placeholder = "Any date",
  className,
  id,
}: DateRangePickerProps) {
  const [open, setOpen] = React.useState(false);

  const label = value?.from
    ? value.to
      ? `${format(value.from, "dd MMM")} – ${format(value.to, "dd MMM")}`
      : format(value.from, "dd MMM yyyy")
    : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          className={cn(
            "h-9 justify-start gap-2 text-left font-normal",
            !label && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="size-4" />
          {label ?? <span>{placeholder}</span>}
          {label ? (
            <X
              className="ml-1 size-3.5 opacity-60 hover:opacity-100"
              onClick={(e) => {
                e.stopPropagation();
                onChange?.(undefined);
              }}
            />
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          selected={value}
          onSelect={(range) => onChange?.(range)}
          numberOfMonths={2}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
}
DateRangePicker.displayName = "DateRangePicker";

export { DateRangePicker };
